import { create } from "zustand";
import type {
  TranscriptionStreamText,
  TranscriptPartialMessage,
} from "@/hooks/transcriber/types";

/**
 * Partials whose payload is the whole in-progress utterance (replace), rather
 * than a word delta for its item (append, OpenAI). Deepgram interims are
 * tagged "deepgram" and Speechmatics partials "speechmatics"; a partial with
 * no item id is treated the same way.
 */
const REPLACING_PARTIAL_IDS = new Set(["deepgram", "speechmatics", "partial"]);

/** True when a `transcript:partial` replaces the previous one for its key. */
export const isWholeUtterancePartial = (itemId?: string): boolean =>
  REPLACING_PARTIAL_IDS.has(itemId || "partial");

const joinPartials = (items: Record<string, string>): string =>
  Object.values(items)
    .map((text) => text.trim())
    .filter(Boolean)
    .join(" ");

/**
 * A stretch of the recording the live transcript did not hear (screen lock,
 * page freeze, network drop). "adding": its audio is being sent again and
 * its lines are on the way. "paused": too long to send again; the note will
 * still have it (from the full recording). Display only, never saved.
 */
export interface LiveTranscriptGap {
  id: number;
  /** Recording time (seconds) where the gap starts and ends. */
  from: number;
  to: number;
  status: "adding" | "paused";
  /** Shown after this many finished segments (where the gap began). */
  afterIndex: number;
}

export interface TranscriptionStreamingState {
  /** Finished segments, in order. */
  transcriptionStreamText: TranscriptionStreamText[];
  /**
   * Words still being recognised after the finished segments ("volatile"
   * text, as on iOS). Display only: never part of the saved transcript.
   */
  partialText: string;
  partialItems: Record<string, string>;
  gaps: LiveTranscriptGap[];
  /** Adds a gap where the finished segments end now; returns its id. */
  addGap: (gap: Omit<LiveTranscriptGap, "id" | "afterIndex">) => number;
  removeGap: (id: number) => void;
  /** Turns a gap into a "Paused …" marker (it could not be added). */
  markGapPaused: (id: number) => void;
  /**
   * The gap's lines arrived separately (transcribed from its audio): they
   * go in where the gap is, and the gap line goes.
   */
  fillGap: (id: number, segments: TranscriptionStreamText[]) => void;
  setStreamText: (data: TranscriptionStreamText) => void;
  /** Applies a `transcript:partial` payload to the in-progress text. */
  setPartialText: (data: TranscriptPartialMessage) => void;
  /**
   * A segment finished: drop its own in-progress words, and any
   * whole-sentence partial (which always belongs to the current segment).
   */
  settlePartialText: (itemId?: string) => void;
  clearPartialText: () => void;
  resetStreamText: () => void;
  finishSteamText: () => void;
  isLoading: boolean;
}

let nextGapId = 0;

export const useTranscriptionStreamingStore =
  create<TranscriptionStreamingState>()((set, get) => ({
    transcriptionStreamText: [],
    partialText: "",
    partialItems: {},
    gaps: [],
    isLoading: false,
    addGap: (gap): number => {
      nextGapId += 1;
      const id = nextGapId;
      set({
        gaps: [
          ...get().gaps,
          { ...gap, id, afterIndex: get().transcriptionStreamText.length },
        ],
      });
      return id;
    },
    markGapPaused: (id: number): void => {
      if (!get().gaps.some((gap) => gap.id === id)) return;
      set({
        gaps: get().gaps.map((gap) =>
          gap.id === id ? { ...gap, status: "paused" } : gap,
        ),
      });
    },
    fillGap: (id: number, segments: TranscriptionStreamText[]): void => {
      const gap = get().gaps.find((item) => item.id === id);
      if (!gap) return;
      const lines = get().transcriptionStreamText;
      const at = Math.min(gap.afterIndex, lines.length);
      set({
        transcriptionStreamText: [
          ...lines.slice(0, at),
          ...segments,
          ...lines.slice(at),
        ],
        // Later gaps keep their place after the inserted lines.
        gaps: get()
          .gaps.filter((item) => item.id !== id)
          .map((item) =>
            item.afterIndex > at || (item.afterIndex === at && item.id > id)
              ? { ...item, afterIndex: item.afterIndex + segments.length }
              : item,
          ),
      });
    },
    removeGap: (id: number): void => {
      if (!get().gaps.some((gap) => gap.id === id)) return;
      set({ gaps: get().gaps.filter((gap) => gap.id !== id) });
    },
    setStreamText: (data: TranscriptionStreamText): void => {
      set({
        isLoading: true,
        transcriptionStreamText: [...get().transcriptionStreamText, data],
      });
    },
    setPartialText: (data: TranscriptPartialMessage): void => {
      if (!data?.transcription) return;
      const key = data.itemId || "partial";
      const items = get().partialItems;
      const next = {
        ...items,
        [key]: REPLACING_PARTIAL_IDS.has(key)
          ? data.transcription
          : (items[key] ?? "") + data.transcription,
      };
      set({
        isLoading: true,
        partialItems: next,
        partialText: joinPartials(next),
      });
    },
    settlePartialText: (itemId?: string): void => {
      const items = get().partialItems;
      const next = Object.fromEntries(
        Object.entries(items).filter(
          ([key]) => key !== itemId && !REPLACING_PARTIAL_IDS.has(key),
        ),
      );
      if (Object.keys(next).length === Object.keys(items).length) return;
      set({ partialItems: next, partialText: joinPartials(next) });
    },
    clearPartialText: (): void => {
      if (!get().partialText && !Object.keys(get().partialItems).length) return;
      set({ partialItems: {}, partialText: "" });
    },
    resetStreamText: (): void => {
      set({
        isLoading: false,
        transcriptionStreamText: [],
        partialItems: {},
        partialText: "",
        gaps: [],
      });
    },
    finishSteamText: (): void => {
      set({
        isLoading: false,
        partialItems: {},
        partialText: "",
      });
    },
  }));
